import { PharmacySelect, PharmacyPagination, PharmacyTabs } from './shared/PharmacyUI';
import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, CircularProgress, Alert, Dialog, Menu, MenuItem,
  Checkbox, useMediaQuery, Tooltip as MuiTooltip
} from '@mui/material';
import {
  FilterList, Search, ExpandMore, FileDownload
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';
import api from '../services/api';

import ReportModal from './ReportModal';
import ReportModalRedistribucion from './ReportModalRedistribucion';
import { HeaderIOS, IOS } from './shared/MenuLayout';

import AlmacenDashboard from './shared/AlmacenDashboard';
import './shared/Almacen.css';

// ── Colores indicadores (mismos que el resto del sistema) ─────────────────────
const IND_CFG = {
  'SOBRESTOCK':   { bg:'#EDFAF2', border:'#B7EFD0', text:'#065F46', dot:'#059669' },
  'NORMOSTOCK':   { bg:'#EDFAF2', border:'#B7EFD0', text:'#166534', dot:'#22C55E' },
  'SUBSTOCK':     { bg:'#FFF8E6', border:'#FFE9A0', text:'#9A3412', dot:'#F97316' },
  'DESABASTECIDO':{ bg:'#FFF0EF', border:'#FFD4D1', text:'#991B1B', dot:'#EF4444' },
  'SIN ROTACION': { bg:'#F3F4F6', border:'#D1D5DB', text:'#4B5563', dot:'#9CA3AF' },
  'SIN CONSUMO':  { bg:'#E5E7EB', border:'#C4C9D4', text:'#374151', dot:'#6B7280' },
  'NO APLICA':    { bg:'#F4F2FA', border:'#DED8EC', text:'#4C1D95', dot:'#7C3AED' },
};
const getInd = (v) => IND_CFG[v?.toUpperCase()] || { bg:IOS.bg, border:IOS.separator, text:IOS.label2, dot:IOS.label3 };

const ACC_CFG = {
  'ABASTECER TOTALMENTE':   { bg:'#EBF5FF', text:'#1E40AF' },
  'ABASTECER PARCIALMENTE': { bg:'#FFF5E6', text:'#9A3412' },
  'SIN STOCK EN UE':        { bg:'#FEE2E2', text:'#991B1B' },
};
const getAcc = (v) => ACC_CFG[v?.toUpperCase()] || { bg:IOS.bg, text:IOS.label2 };

// ── Pill indicador ────────────────────────────────────────────────────────────
const IndPill = ({ valor }) => {
  const cfg = getInd(valor);
  return (
    <Box className="almacen-indicator-pill" sx={{ display:'inline-flex', alignItems:'center', gap:0.5, px:0.8, py:0.2,
      borderRadius:'20px', bgcolor:cfg.bg, border:`1px solid ${cfg.border}` }}>
      <Box sx={{ width:5, height:5, borderRadius:'50%', bgcolor:cfg.dot }} />
      <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:cfg.text, whiteSpace:'nowrap' }}>{valor||'—'}</Typography>
    </Box>
  );
};

// ── Sel ───────────────────────────────────────────────────────────────────────
const Sel = PharmacySelect;

// ── Tabla genérica ────────────────────────────────────────────────────────────
const Tabla = ({ headers, children, maxH='calc(100vh - 400px)' }) => (
  <Box sx={{ borderRadius:'14px', border:`1px solid ${IOS.separator}`, overflow:'hidden' }}>
    <Box className="pharmacy-scroll" tabIndex={0} role="region" aria-label="Tabla de resultados, desplazamiento horizontal" sx={{ overflowX:'auto', overflowY:'auto', maxHeight:maxH,
      '&::-webkit-scrollbar':{ width:4, height:4 },
      '&::-webkit-scrollbar-thumb':{ bgcolor:IOS.separator, borderRadius:4 } }}>
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.75rem', minWidth:1100 }}>
        <thead>
          <tr>
            {headers.map((h,i) => (
              <th key={i} style={{ padding:'9px 12px', textAlign:h.right?'right':'left',
                fontSize: '0.72rem', fontWeight:700, color:IOS.label2, letterSpacing:'0.06em',
                textTransform:'uppercase', backgroundColor:IOS.surface,
                borderBottom:`1px solid ${IOS.separator}`, position:'sticky', top:0,
                whiteSpace:'nowrap', zIndex:1 }}>{h.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </Box>
  </Box>
);

const TR = ({ children }) => {
  const [h, setH] = useState(false);
  return (
    <tr onMouseEnter={()=>setH(true)} onMouseLeave={()=>setH(false)}
      style={{ backgroundColor:h?IOS.bg:IOS.surface, transition:'background 0.1s',
        borderBottom:`1px solid ${IOS.separator}` }}>
      {children}
    </tr>
  );
};
const Paginacion = PharmacyPagination;

// ═════════════════════════════════════════════════════════════════════════════
const Almacen = () => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const ueId = user?.ueIdEstablecimiento || user?.UeIdEstablecimiento || 0;

  const reduced = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [tab, setTab] = useState(0);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [errores, setErrores] = useState({});
  const [procesado, setProcesado] = useState(false);
  const familyRequest = useRef(0);
  const dashboardRequest = useRef(0);
  const redistribRequest = useRef(0);
  const fail = (key, message) => setErrores(prev=>({...prev,[key]:message}));
  const [cargando, setCargando] = useState(false);

  // Dashboard
  const [resumen, setResumen]   = useState({ almacenes:0, centros:0, hospitales:0, puestosSalud:0 });
  const [datosDME, setDatosDME] = useState({ periodos:[], valores:[] });
  const [mesSelDME, setMesSelDME] = useState(null); // índice dentro de los últimos 12 — null = mes actual
  const [datosICI, setDatosICI] = useState({ datos:[], totales:{ cumplio:0, atrasado:0, adelantado:0, sinRegistro:0, total:0 } });
  const [filtroICI, setFiltroICI] = useState(null); // 'Cumplió' | 'Atrasado' | 'Adelantado' | 'Sin Registro' | null
  const [substock, setSubstock] = useState([]);

  // Distribución
  const [mesesCob, setMesesCob]   = useState(3);
  const [porcentaje, setPct] = useState(50);
  const [datosDistrib, setDistrib] = useState([]);
  const [filtrados, setFiltrados]  = useState([]);
  const [filtros, setFiltros]      = useState({ ipress:[], esencial:[], estrategico:[], producto:'', indicador:[], accion:[] });
  const [colapsado, setColapsado]  = useState(false);
  const [pg, setPg]                = useState(1);
  const [rpp, setRpp]              = useState(()=>window.matchMedia('(max-width:599px)').matches?10:20);
  const [ipressUnicos, setIpressU] = useState([]);
  const [anchorF, setAnchorF]      = useState(null);
  const [filtroActivo, setFiltroA] = useState(null);
  const [modalDist, setModalDist]  = useState(false);

  // Redistribución
  const [datosRedist, setRedist]         = useState([]);
  const [filtradosRedist, setFiltRedist] = useState([]);
  const [cargandoR, setCargandoR]        = useState(true);
  const [donantes, setDonantes]          = useState([]);
  const [donante, setDonante]            = useState('TODOS');
  const [pgR, setPgR]                    = useState(1);
  const [rppR, setRppR]                  = useState(()=>window.matchMedia('(max-width:599px)').matches?10:20);
  const [modalRedist, setModalRedist]    = useState(false);

  // Familia fusionada — popup de otras presentaciones con stock en almacén
  const [familiaModal, setFamiliaModal] = useState(null); // { nivel:'ipress'|'almacen', codigo, producto, data:null|[], loading }
  const verFamilia = async (nivel, codigo, producto, agrupador) => {
    const requestId = ++familyRequest.current;
    setFamiliaModal({ nivel, codigo, producto, requestId, data:null, loading:true });
    try {
      const url = nivel === 'ipress' ? '/farmacia/almacen/familia-ipress-detalle' : '/farmacia/almacen/familia-almacen-detalle';
      const params = nivel === 'ipress'
        ? { codigoMed:codigo, agrupador, ueId }
        : { codigoMed:codigo, idAlmacenUE: user?.AlmacenUe || user?.almacenUe || 0 };
      const r = await api.get(url, { params });
      if (!r.data.success) throw new Error('No se pudo consultar el stock');
      setFamiliaModal(prev => prev?.requestId===requestId ? ({ ...prev, data:r.data.data, loading:false }) : prev);
    } catch(e) { console.error(e); setFamiliaModal(prev => prev?.requestId===requestId ? ({ ...prev, data:[], loading:false, error:true }) : prev); }
  };

  const valoresFijos = {
    esencial:   ['ME','OTRO'],
    estrategico:['SÍ','NO'],
    indicador:  ['DESABASTECIDO','SUBSTOCK','NORMOSTOCK','SOBRESTOCK','SIN ROTACION','SIN CONSUMO','NO APLICA'],
    accion:     ['SIN STOCK EN UE','SIN NECESIDAD DE ABASTECER','ABASTECER TOTALMENTE','ABASTECER PARCIALMENTE','REVISAR CÁLCULO'],
  };

  const handleLogout = () => { authService.logout(); navigate('/login'); };

  useEffect(() => {
    if (tab===0) cargarDashboard();
    if (tab===2) cargarRedistribucion();
  }, [tab]);

  const cargarDashboard = async () => {
    const requestId=++dashboardRequest.current;
    setDashboardLoading(true); fail(0, null); setMesSelDME(null); setFiltroICI(null);
    try {
      const results = await Promise.all([
        api.get(`/farmacia/almacen/resumen-ue?ueId=${ueId}`),
        api.get(`/farmacia/almacen/disponibilidad-dme?ueId=${ueId}`),
        api.get(`/farmacia/almacen/cumplimiento-ici?ueId=${ueId}`),
        api.get(`/farmacia/almacen/substock?ueId=${ueId}`),
      ]);
      if(requestId!==dashboardRequest.current)return;
      if (results.some(r=>!r.data.success)) throw new Error('Consulta incompleta');
      setResumen(results[0].data.data);
      setDatosDME({periodos:results[1].data.periodos,valores:results[1].data.valores});
      setDatosICI(results[2].data); setSubstock(results[3].data.data);
    } catch(e) { if(requestId===dashboardRequest.current) fail(0,'No pudimos cargar el resumen del almacén. Inténtalo de nuevo.'); }
    finally { if(requestId===dashboardRequest.current)setDashboardLoading(false); }
  };

  const procesarDistribucion = async () => {
    if (cargando) return;
    setCargando(true); fail(1,null);
    setFiltros({ ipress:[], esencial:[], estrategico:[], producto:'', indicador:[], accion:[] });
    try {
      const periodo = `${new Date().getFullYear()}${String(new Date().getMonth()+1).padStart(2,'0')}`;
      const almacenUe = user?.AlmacenUe || user?.almacenUe || 0;
      const r = await api.get('/farmacia/almacen/plan-distribucion', { params:{ ueId, almacenUe, periodo, mesesCobertura:mesesCob, porcentaje } });
      if (r.data.success) {
        setDistrib(r.data.data); setFiltrados(r.data.data); setPg(1); setProcesado(true);
        setIpressU([...new Set(r.data.data.map(d=>d.Establecimiento))].sort());
        setColapsado(true);
      } else throw new Error('Consulta fallida');
    } catch(e) { fail(1,'No pudimos generar el plan. Vuelve a presionar Procesar.'); } finally { setCargando(false); }
  };

  const filtrarConFiltros = (f) => {
    let res = [...datosDistrib];
    if (f.ipress.length)     res = res.filter(d=>f.ipress.includes(d.Establecimiento));
    if (f.esencial.length)   res = res.filter(d=>f.esencial.includes(d.ESENCIAL));
    if (f.estrategico.length) {
      const normalizar = valor => {
        const texto = String(valor ?? '').trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
        if (texto === 'ESTRATEGICO') return 'SI';
        if (texto === 'NO ESTRATEGICO') return 'NO';
        return texto;
      };
      const seleccionados = f.estrategico.map(normalizar);
      res = res.filter(d => seleccionados.includes(normalizar(d.FM_MEDEST)));
    }
    if (f.producto)          res = res.filter(d=>d.PRODUCTO?.toLowerCase().includes(f.producto.toLowerCase()));
    if (f.indicador.length)  res = res.filter(d=>f.indicador.includes(d.Indicador_DIGEMID));
    if (f.accion.length)     res = res.filter(d=>f.accion.includes(d.AccionSugerida));
    setFiltrados(res); setPg(1);
  };

  const aplicarFiltro = (col, val) => {
    const nuevo = filtros[col].includes(val) ? filtros[col].filter(v=>v!==val) : [...filtros[col], val];
    const nf = { ...filtros, [col]:nuevo };
    setFiltros(nf); filtrarConFiltros(nf);
  };

  const cargarRedistribucion = async () => {
    const requestId=++redistribRequest.current;
    setCargandoR(true); fail(2,null);
    try {
      const r = await api.get('/farmacia/almacen/traspasos-sugeridos', { params:{ ueId } });
      if(requestId!==redistribRequest.current)return;
      if (r.data.success) {
        setRedist(r.data.data); setFiltRedist(r.data.data); setDonante('TODOS'); setPgR(1);
        setDonantes([...new Set(r.data.data.map(d=>d.DONANTE))].sort());
      } else throw new Error('Consulta fallida');
    } catch(e) { if(requestId===redistribRequest.current)fail(2,'No pudimos cargar los traspasos sugeridos. Inténtalo de nuevo.'); } finally { if(requestId===redistribRequest.current)setCargandoR(false); }
  };

  const filtrarDonante = (d) => {
    setDonante(d); setPgR(1);
    setFiltRedist(d==='TODOS' ? datosRedist : datosRedist.filter(r=>r.DONANTE===d));
  };

  const datosPag  = filtrados.slice((pg-1)*rpp, pg*rpp);
  const datosRPag = filtradosRedist.slice((pgR-1)*rppR, pgR*rppR);

  return (
    <>
      <Box className="pharmacy-page almacen-page" sx={{ minHeight:'100vh', bgcolor:IOS.bg, overflowX:'hidden', width:'100%' }}>
      <HeaderIOS titulo="Almacén" subtitulo="Farmacia"
        onHome={()=>navigate('/farmacia')} onLogout={handleLogout}
        nombreCompleto={user?.nombreCompleto||user?.usuario||''}
        avatarLetra={(user?.nombreCompleto||user?.usuario||'U').charAt(0).toUpperCase()} />

      {/* Tabs */}
      <Box className="pharmacy-tabs-row"><PharmacyTabs value={tab} onChange={value=>{if(value===tab)return;if(value===2)setCargandoR(true);if(value===0)setDashboardLoading(true);setTab(value);}}  options={['Dashboard','Distribución','Redistribución']}/></Box>

      <Box className="pharmacy-content" sx={{ maxWidth:1600, mx:'auto', px:{ xs:1.5, sm:2.6 }, pt:2.5, pb:6 }}>

        <Box className="almacen-intro">
          <Box><Typography component="h1">{['Tu red, de un vistazo','Plan de distribución','Redistribución entre IPRESS'][tab]}</Typography>
          <Typography>{['Disponibilidad, cumplimiento y prioridades de abastecimiento.','Define la cobertura y revisa el stock sugerido para cada establecimiento.','Identifica excedentes y oportunidades para abastecer otros establecimientos.'][tab]}</Typography></Box>
          {tab!==1 && <button className="almacen-secondary" disabled={tab===0?dashboardLoading:cargandoR} onClick={tab===0?cargarDashboard:cargarRedistribucion}>Actualizar</button>}
        </Box>
        {errores[tab] && <Alert severity="error" sx={{mb:2,borderRadius:3}}>{errores[tab]}</Alert>}
        {tab===0 && (dashboardLoading ? <Box className="almacen-loading" role="status"><CircularProgress size={26}/><Typography>Consultando el almacén…</Typography></Box> : !errores[0] &&
          <AlmacenDashboard resumen={resumen} datosDME={datosDME} datosICI={datosICI} substock={substock} mesSelDME={mesSelDME} setMesSelDME={setMesSelDME} filtroICI={filtroICI} setFiltroICI={setFiltroICI}/>
        )}

        {/* ══ TAB 1: DISTRIBUCIÓN ══════════════════════════════════════════ */}
        {tab===1 && (
          <Box>
            {/* Parámetros */}
            <Box className="pharmacy-card" sx={{ bgcolor:IOS.surface, borderRadius:'16px', p:2,
              boxShadow:'0 1px 4px rgba(0,0,0,0.07)', border:`1px solid ${IOS.separator}`, mb:2 }}>
              <Box className="pharmacy-layout" sx={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                cursor:'pointer', mb:colapsado?0:2 }} role="button" tabIndex={0} aria-expanded={!colapsado} onKeyDown={e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();setColapsado(!colapsado);}}} onClick={()=>setColapsado(!colapsado)}>
                <Typography sx={{ fontSize:'0.82rem', fontWeight:700, color:IOS.label }}>
                  Parámetros de Distribución
                </Typography>
                <Box className="pharmacy-layout" sx={{ display:'flex', alignItems:'center', gap:1 }}>
                  {colapsado && (
                    <Typography sx={{ fontSize:'0.72rem', color:IOS.label2 }}>
                      {mesesCob} {mesesCob===1?'mes':'meses'} · {porcentaje}%
                    </Typography>
                  )}
                  <ExpandMore sx={{ fontSize:20, color:IOS.label2,
                    transform:colapsado?'rotate(0deg)':'rotate(180deg)', transition:'transform 0.3s' }} />
                </Box>
              </Box>

              {!colapsado && (
                <Box className="pharmacy-layout pharmacy-toolbar" sx={{ display:'flex', gap:2, flexWrap:'wrap', alignItems:'flex-end' }}>
                  <Sel label="Meses de Cobertura" value={mesesCob} onChange={e=>setMesesCob(Number(e.target.value))} disabled={cargando} w={180}>
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(m=><option key={m} value={m}>{m} {m===1?'mes':'meses'}</option>)}
                  </Sel>
                  <Sel label="% Stock a Distribuir" value={porcentaje} onChange={e=>setPct(Number(e.target.value))} disabled={cargando} w={180}>
                    {[10,20,30,40,50,60,70,80,90,100].map(p=><option key={p} value={p}>{p}%</option>)}
                  </Sel>
                  <Box className="pharmacy-layout pharmacy-action" component="button" onClick={procesarDistribucion} disabled={cargando}
                    sx={{ height:34, px:2.5, borderRadius:'10px', border:'none',
                      bgcolor:IOS.blue, cursor:cargando?'wait':'pointer',
                      display:'flex', alignItems:'center', gap:0.8, transition:'all 0.2s',
                      '&:hover':{ filter:'brightness(0.92)' } }}>
                    {cargando ? <CircularProgress size={13} sx={{ color:'white' }} />
                      : <Typography sx={{ fontSize:'0.8rem', fontWeight:700, color:'white' }}>Procesar</Typography>}
                  </Box>
                  <Box className="pharmacy-layout" sx={{ display:'flex', alignItems:'center', gap:0.8, px:1.5, py:0.6,
                    bgcolor:IOS.bg, borderRadius:'20px', border:`1px solid ${IOS.separator}` }}>
                    <Box sx={{ width:7, height:7, borderRadius:'50%', bgcolor:IOS.blue }} />
                    <Typography sx={{ fontSize:'0.72rem', fontWeight:600, color:IOS.label2 }}>
                      {filtrados.length.toLocaleString()} registros
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Filtros */}
            {datosDistrib.length > 0 && (
              <Box className="pharmacy-card" sx={{ bgcolor:IOS.surface, borderRadius:'16px', p:2,
                boxShadow:'0 1px 4px rgba(0,0,0,0.07)', border:`1px solid ${IOS.separator}`, mb:2 }}>
                <Box className="pharmacy-layout" sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:1.5 }}>
                  <Box className="pharmacy-layout" sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
                    <Typography sx={{ fontSize:'0.78rem', fontWeight:700, color:IOS.label }}>Filtros</Typography>
                    <Box sx={{ px:1, py:0.2, borderRadius:'20px', bgcolor:`${IOS.blue}15` }}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:IOS.blue }}>
                        {filtrados.length} / {datosDistrib.length}
                      </Typography>
                    </Box>
                  </Box>
                  <Box className="pharmacy-layout" sx={{ display:'flex', gap:1, alignItems:'center' }}>
                    {Object.values(filtros).some(v=>Array.isArray(v)?v.length>0:v) && (
                      <Box className="pharmacy-action" component="button" onClick={()=>{const f={ipress:[],esencial:[],estrategico:[],producto:'',indicador:[],accion:[]};setFiltros(f);filtrarConFiltros(f);}}
                        sx={{ height:28, px:1.5, borderRadius:'8px', border:`1px solid ${IOS.separator}`,
                          bgcolor:IOS.surface, cursor:'pointer', fontSize:'0.72rem', color:IOS.label2 }}>
                        Limpiar
                      </Box>
                    )}
                    <Box className="pharmacy-layout pharmacy-action" component="button" aria-label="Descargar distribución" onClick={()=>setModalDist(true)} disabled={filtrados.length===0}
                      sx={{ width:32, height:32, borderRadius:'50%', border:'none',
                        bgcolor:IOS.green, cursor:'pointer', display:'flex',
                        alignItems:'center', justifyContent:'center', transition:'all 0.15s',
                        '&:hover':{ filter:'brightness(0.9)' } }}>
                      <FileDownload sx={{ fontSize:16, color:'white' }} />
                    </Box>
                  </Box>
                </Box>
                <Box className="pharmacy-layout" sx={{ display:'flex', gap:1, flexWrap:'wrap', alignItems:'center' }}>
                  {[['ipress','IPRESS'],['esencial','Esencial'],['estrategico','Estratégico'],['indicador','Indicador'],['accion','Acción']].map(([key,lbl])=>(
                    <Box className="pharmacy-layout pharmacy-action" key={key} component="button" onClick={e=>{setAnchorF(e.currentTarget);setFiltroA(key);}}
                      sx={{ height:30, px:1.5, borderRadius:'20px', border:`1px solid ${filtros[key].length>0?IOS.blue:IOS.separator}`,
                        bgcolor:filtros[key].length>0?`${IOS.blue}10`:IOS.surface, cursor:'pointer',
                        display:'flex', alignItems:'center', gap:0.6, transition:'all 0.15s' }}>
                      <FilterList sx={{ fontSize:13, color:filtros[key].length>0?IOS.blue:IOS.label2 }} />
                      <Typography sx={{ fontSize:'0.72rem', fontWeight:filtros[key].length>0?700:500,
                        color:filtros[key].length>0?IOS.blue:IOS.label2 }}>
                        {lbl}{filtros[key].length>0?` (${filtros[key].length})`:''}
                      </Typography>
                    </Box>
                  ))}
                  {/* Búsqueda producto */}
                  <Box sx={{ position:'relative', flex:1, minWidth:160 }}>
                    <Search sx={{ position:'absolute', left:8, top:'50%', transform:'translateY(-50%)',
                      fontSize:15, color:IOS.label2, pointerEvents:'none' }} />
                    <input aria-label="Buscar producto" type="text" placeholder="Buscar producto..." value={filtros.producto}
                      onChange={e=>{const nf={...filtros,producto:e.target.value};setFiltros(nf);filtrarConFiltros(nf);}}
                      style={{ width:'100%', height:30, paddingLeft:28, paddingRight:10,
                        border:`1px solid ${IOS.separator}`, borderRadius:20, fontSize:'0.72rem',
                        color:IOS.label, backgroundColor:IOS.surface, outline:'none',
                        boxSizing:'border-box', fontFamily:'inherit' }}
                      onFocus={e=>e.target.style.borderColor=IOS.blue}
                      onBlur={e=>e.target.style.borderColor=IOS.separator} />
                  </Box>
                </Box>
              </Box>
            )}

            {/* Menú filtro */}
            <Menu transitionDuration={0} disableScrollLock anchorEl={anchorF} open={Boolean(anchorF)} onClose={()=>setAnchorF(null)}
              PaperProps={{ sx:{ maxHeight:360, width:260, borderRadius:'18px', p:0.5,
                boxShadow:'0 8px 24px rgba(0,0,0,0.12)' } }}>
              {filtroActivo && (filtroActivo==='ipress'?ipressUnicos:valoresFijos[filtroActivo]||[]).map((val,i)=>(
                <MenuItem key={i} onClick={()=>aplicarFiltro(filtroActivo,val)} dense sx={{ py:0.8 }}>
                  <Checkbox checked={filtros[filtroActivo]?.includes(val)} size="small" sx={{ p:0.5, mr:1, color:IOS.blue, '&.Mui-checked':{ color:IOS.blue } }} />
                  <Typography sx={{ fontSize:'0.8rem' }}>{val}</Typography>
                </MenuItem>
              ))}
              <Box sx={{ p:1, borderTop:`1px solid ${IOS.separator}` }}>
                <Box className="pharmacy-action" component="button" onClick={()=>setAnchorF(null)}
                  sx={{ width:'100%', height:30, borderRadius:'8px', border:'none',
                    bgcolor:IOS.blue, cursor:'pointer', color:'white', fontSize:'0.78rem', fontWeight:600 }}>
                  Cerrar
                </Box>
              </Box>
            </Menu>

            {/* Tabla distribución */}
            {cargando
              ? <Box sx={{ py:10, textAlign:'center' }}><CircularProgress sx={{ color:IOS.blue }} /></Box>
              : filtrados.length > 0
                ? <div className="almacen-distribution-responsive">
                    {/* Móvil — cards */}
                    <Box className="almacen-distribution-cards">
                      {datosPag.map((row,i) => {
                        
                        const acc = getAcc(row.AccionSugerida);
                        return (
                          <Box className="almacen-product-card" key={i} sx={{ bgcolor:IOS.surface, borderRadius:'14px', p:2, mb:1.2,
                            border:`1px solid ${IOS.separator}` }}>
                            <Box className="pharmacy-layout" sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:1, mb:0.8 }}>
                              <Box sx={{ minWidth:0 }}>
                                <Typography sx={{ fontWeight:700, fontSize:'0.8rem', color:'#1F2937', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{row.PRODUCTO}</Typography>
                                <Typography sx={{ fontSize: '0.72rem', color:'#6B7280', fontFamily:'monospace' }}>{row.CODIGO_MED} · {row.Establecimiento}</Typography>
                              </Box>
                              <Box className="pharmacy-layout" sx={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:0.4 }}>
                                <IndPill valor={row.Indicador_DIGEMID} />
                                {row.Indicador_DIGEMID==='DESABASTECIDO' && (row.StockOtrasPresentaciones||0) > 0 && (
                                  <Box component="button" className="almacen-family-button" onClick={()=>verFamilia('ipress', row.CODIGO_MED, row.PRODUCTO, row.Agrupador)}
                                    sx={{ display:'inline-flex', alignItems:'center', gap:0.3, px:0.7, py:0.15, borderRadius:'20px',
                                      bgcolor:'#FFF3CD', border:'1px solid #FFE066', cursor:'pointer' }}>
                                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#8B6914" strokeWidth="2.5" strokeLinecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                                    <Typography sx={{ fontSize: '0.72rem', fontWeight:800, color:'#8B6914' }}>{row.StockOtrasPresentaciones.toLocaleString()}</Typography>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                            <Box className="pharmacy-layout" sx={{ display:'flex', gap:0.6, flexWrap:'wrap', mb:1 }}>
                              <Box sx={{ display:'inline-flex', px:0.8, py:0.2, borderRadius:'20px',
                                bgcolor:row.ESENCIAL==='ME'?'#DCFCE7':'#F3F4F6',
                                border:`1px solid ${row.ESENCIAL==='ME'?'#86EFAC':IOS.separator}` }}>
                                <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:row.ESENCIAL==='ME'?'#166534':IOS.label2 }}>{row.ESENCIAL}</Typography>
                              </Box>
                              <Box sx={{ display:'inline-flex', px:0.8, py:0.2, borderRadius:'20px', bgcolor:IOS.bg, border:`1px solid ${IOS.separator}` }}>
                                <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:'#6B7280' }}>{row.FM_MEDEST}</Typography>
                              </Box>
                            </Box>
                            <Box className="pharmacy-layout" sx={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:0.6, mb:1 }}>
                              {[['CPMA',parseFloat(row.CPMA||0).toFixed(2)],['MSD',parseFloat(row.MSD||0).toFixed(2)],['Stock Act.',row.Stock_Actual]].map(([l,v],j)=>(
                                <Box key={j} sx={{ bgcolor:IOS.bg, borderRadius:'8px', px:0.5, py:0.5, textAlign:'center' }}>
                                  <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:'#6B7280', textTransform:'uppercase' }}>{l}</Typography>
                                  <Typography sx={{ fontSize:'0.74rem', fontWeight:700, color:'#1F2937' }}>{v}</Typography>
                                </Box>
                              ))}
                            </Box>
                            <Box className="pharmacy-layout" sx={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:0.6, mb:1 }}>
                              {[['Requerimiento',parseFloat(row.Requerimiento||0).toFixed(2)],['Stock UE',row.Stock_UE],['Distribuible',row.StockDistribuible],['Sugerido',row.StockSugerido]].map(([l,v],j)=>(
                                <Box key={j} sx={{ bgcolor:IOS.bg, borderRadius:'8px', px:0.5, py:0.5, textAlign:'center' }}>
                                  <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:'#6B7280', textTransform:'uppercase' }}>{l}</Typography>
                                  <Typography sx={{ fontSize:'0.74rem', fontWeight:700, color:'#1F2937' }}>{v}</Typography>
                                </Box>
                              ))}
                            </Box>
                            {(Number(row.StockDistribuible)===0) && (row.StockAlmacenOtrasPresentaciones||0) > 0 && (
                              <Box component="button" className="almacen-family-button" onClick={()=>verFamilia('almacen', row.CODIGO_MED, row.PRODUCTO)}
                                sx={{ display:'inline-flex', alignItems:'center', gap:0.4, px:0.9, py:0.3, borderRadius:'20px', mb:1,
                                  bgcolor:'#FFF3CD', border:'1px solid #FFE066', cursor:'pointer' }}>
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#8B6914" strokeWidth="2.5" strokeLinecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                                <Typography sx={{ fontSize: '0.72rem', fontWeight:800, color:'#8B6914' }}>{row.StockAlmacenOtrasPresentaciones.toLocaleString()} en otra presentación · Ver</Typography>
                              </Box>
                            )}
                            <Box className="pharmacy-layout" sx={{ display:'flex', gap:0.6, alignItems:'center', flexWrap:'wrap' }}>
                              <Box sx={{ display:'inline-flex', px:1, py:0.3, borderRadius:'20px', bgcolor:'#D1FAE5', border:'1px solid #86EFAC' }}>
                                <Typography sx={{ fontSize:'0.7rem', fontWeight:800, color:'#166534' }}>Sugerido: {row.StockSugerido}</Typography>
                              </Box>
                              <Box sx={{ display:'inline-flex', px:1, py:0.3, borderRadius:'20px', bgcolor:acc.bg, border:`1px solid ${acc.text}30` }}>
                                <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:acc.text }}>{row.AccionSugerida}</Typography>
                              </Box>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>

                    {/* Desktop — tabla */}
                    <Box className="almacen-distribution-table">
                      <Tabla headers={[
                        {label:'Establecimiento'},{label:'Producto / código'},
                        {label:'Esencial'},{label:'Estratégico'},
                        {label:'CPMA',right:true},{label:'MSD',right:true},
                        {label:'Indicador'},{label:'Stock actual',right:true},
                        {label:'Requerimiento',right:true},{label:'Stock UE',right:true},
                        {label:'Distribuible',right:true},{label:'Sugerido',right:true},
                        {label:'Acción'},
                      ]}>
                        {datosPag.map((row,i) => {
                          
                          const acc = getAcc(row.AccionSugerida);
                          return (
                            <TR key={i}>
                              <td className="almacen-destination"><span>{row.Establecimiento}</span></td>
                              <td className="almacen-product-identity"><strong>{row.PRODUCTO}</strong><span>{row.CODIGO_MED}</span></td>
                              <td style={{ padding:'8px 12px' }}>
                                <Box sx={{ display:'inline-flex', alignItems:'center', gap:0.5,
                                  px:0.8, py:0.2, borderRadius:'20px',
                                  bgcolor:row.ESENCIAL==='ME'?'#DCFCE7':'#F3F4F6',
                                  border:`1px solid ${row.ESENCIAL==='ME'?'#86EFAC':IOS.separator}` }}>
                                  <Typography sx={{ fontSize: '0.72rem', fontWeight:700,
                                    color:row.ESENCIAL==='ME'?'#166534':IOS.label2 }}>{row.ESENCIAL}</Typography>
                                </Box>
                              </td>
                              <td style={{ padding:'8px 12px', color:'#4B5563' }}>{row.FM_MEDEST}</td>
                              <td style={{ padding:'8px 12px', textAlign:'right', color:'#6B7280' }}>{parseFloat(row.CPMA||0).toFixed(2)}</td>
                              <td style={{ padding:'8px 12px', textAlign:'right', color:'#6B7280' }}>{parseFloat(row.MSD||0).toFixed(2)}</td>
                              <td style={{ padding:'8px 12px' }}>
                                <Box className="pharmacy-layout" sx={{ display:'flex', alignItems:'center', gap:0.5, flexWrap:'wrap' }}>
                                  <IndPill valor={row.Indicador_DIGEMID} />
                                  {row.Indicador_DIGEMID==='DESABASTECIDO' && (row.StockOtrasPresentaciones||0) > 0 && (
                                    <MuiTooltip title="Ver otras presentaciones con stock en esta IPRESS" arrow placement="top">
                                      <Box component="button" className="almacen-family-button" onClick={()=>verFamilia('ipress', row.CODIGO_MED, row.PRODUCTO, row.Agrupador)}
                                        sx={{ display:'inline-flex', alignItems:'center', gap:0.3, px:0.7, py:0.15, borderRadius:'20px',
                                          bgcolor:'#FFF3CD', border:'1px solid #FFE066', cursor:'pointer', '&:hover':{ bgcolor:'#FFE9A0' } }}>
                                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#8B6914" strokeWidth="2.5" strokeLinecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                                        <Typography sx={{ fontSize: '0.72rem', fontWeight:800, color:'#8B6914' }}>{row.StockOtrasPresentaciones.toLocaleString()}</Typography>
                                      </Box>
                                    </MuiTooltip>
                                  )}
                                </Box>
                              </td>
                              <td style={{ padding:'8px 12px', textAlign:'right', fontWeight:700, color:'#1F2937' }}>{row.Stock_Actual}</td>
                              <td style={{ padding:'8px 12px', textAlign:'right', color:'#6B7280' }}>{parseFloat(row.Requerimiento||0).toFixed(2)}</td>
                              <td style={{ padding:'8px 12px', textAlign:'right', color:'#6B7280' }}>{row.Stock_UE}</td>
                              <td style={{ padding:'8px 12px', textAlign:'right' }}>
                                <Box className="pharmacy-layout" sx={{ display:'flex', alignItems:'center', justifyContent:'flex-end', gap:0.5 }}>
                                  <Typography sx={{ fontSize:'0.73rem', color:'#6B7280' }}>{row.StockDistribuible}</Typography>
                                  {(Number(row.StockDistribuible)===0) && (row.StockAlmacenOtrasPresentaciones||0) > 0 && (
                                    <MuiTooltip title="Ver otras presentaciones con stock en el almacén" arrow placement="top">
                                      <Box component="button" className="almacen-family-button" onClick={()=>verFamilia('almacen', row.CODIGO_MED, row.PRODUCTO)}
                                        sx={{ display:'inline-flex', alignItems:'center', gap:0.3, px:0.7, py:0.15, borderRadius:'20px',
                                          bgcolor:'#FFF3CD', border:'1px solid #FFE066', cursor:'pointer',
                                          '&:hover':{ bgcolor:'#FFE9A0' } }}>
                                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#8B6914" strokeWidth="2.5" strokeLinecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                                        <Typography sx={{ fontSize: '0.72rem', fontWeight:800, color:'#8B6914' }}>{row.StockAlmacenOtrasPresentaciones.toLocaleString()}</Typography>
                                      </Box>
                                    </MuiTooltip>
                                  )}
                                </Box>
                              </td>
                              <td style={{ padding:'8px 12px', textAlign:'right' }}>
                                <Box sx={{ display:'inline-flex', px:1, py:0.25, borderRadius:'20px', bgcolor:'#D1FAE5', border:'1px solid #86EFAC' }}>
                                  <Typography sx={{ fontSize:'0.72rem', fontWeight:800, color:'#166534' }}>{row.StockSugerido}</Typography>
                                </Box>
                              </td>
                              <td style={{ padding:'8px 12px' }}>
                                <Box sx={{ display:'inline-flex', px:0.8, py:0.2, borderRadius:'20px', bgcolor:acc.bg, border:`1px solid ${acc.text}30` }}>
                                  <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:acc.text, whiteSpace:'nowrap' }}>
                                    {row.AccionSugerida}
                                  </Typography>
                                </Box>
                              </td>
                            </TR>
                          );
                        })}
                      </Tabla>
                    </Box>

                    <Paginacion total={filtrados.length} pagina={pg} setPagina={setPg} rpp={rpp} setRpp={setRpp} />
                    <ReportModal open={modalDist} onClose={()=>setModalDist(false)} datos={filtrados}
                      parametros={{ mesesCobertura:mesesCob, porcentaje, usuario:user?.NombreCompleto||'Usuario' }} />
                  </div>
                : !errores[1] && datosDistrib.length===0
                    ? <Box className="pharmacy-card" sx={{ bgcolor:IOS.surface, borderRadius:'16px', py:8, textAlign:'center',
                        border:`1px solid ${IOS.separator}` }}>
                        <FilterList sx={{ fontSize:40, color:IOS.label3, mb:1.5 }} />
                        <Typography sx={{ fontWeight:600, color:IOS.label, mb:0.5 }}>{procesado ? 'Sin productos para distribuir' : 'Prepara tu distribución'}</Typography>
                        <Typography sx={{ color:IOS.label2, fontSize:'0.82rem' }}>
                          {procesado ? 'No hay resultados para los parámetros seleccionados.' : 'Configura los parámetros y presiona Procesar'}
                        </Typography>
                      </Box>
                    : !errores[1] && <Box className="almacen-empty">Ningún producto coincide con los filtros.</Box>
            }
          </Box>
        )}

        {/* ══ TAB 2: REDISTRIBUCIÓN ════════════════════════════════════════ */}
        {tab===2 && (
          <Box>
            {/* Filtros redistribución */}
            <Box className="pharmacy-layout pharmacy-toolbar pharmacy-card" sx={{ bgcolor:IOS.surface, borderRadius:'16px', p:2,
              boxShadow:'0 1px 4px rgba(0,0,0,0.07)', border:`1px solid ${IOS.separator}`,
              mb:2, display:'flex', gap:2, flexWrap:'wrap', alignItems:'flex-end' }}>
              <Sel label="IPRESS Donante" value={donante} onChange={e=>filtrarDonante(e.target.value)}
                disabled={cargandoR} w={280}>
                <option value="TODOS">TODOS</option>
                {donantes.map((d,i)=><option key={i} value={d}>{d}</option>)}
              </Sel>
              <Box className="pharmacy-layout" sx={{ ml:'auto', display:'flex', alignItems:'flex-end', gap:1.5 }}>
                <Box className="pharmacy-layout" sx={{ display:'flex', alignItems:'center', gap:0.8, px:1.5, py:0.6,
                  bgcolor:IOS.bg, borderRadius:'20px', border:`1px solid ${IOS.separator}` }}>
                  <Box sx={{ width:7, height:7, borderRadius:'50%', bgcolor:IOS.green }} />
                  <Typography sx={{ fontSize:'0.72rem', fontWeight:600, color:IOS.label2 }}>
                    {filtradosRedist.length.toLocaleString()} registros
                  </Typography>
                </Box>
                <Box className="pharmacy-layout pharmacy-action" component="button" aria-label="Descargar redistribución" onClick={()=>setModalRedist(true)} disabled={filtradosRedist.length===0}
                  sx={{ width:34, height:34, borderRadius:'50%', border:'none',
                    bgcolor:IOS.green, cursor:'pointer', display:'flex',
                    alignItems:'center', justifyContent:'center', transition:'all 0.15s',
                    opacity:filtradosRedist.length===0?0.4:1,
                    '&:hover':{ filter:'brightness(0.9)' } }}>
                  <FileDownload sx={{ fontSize:16, color:'white' }} />
                </Box>
              </Box>
            </Box>

            {cargandoR
              ? <Box sx={{ py:10, textAlign:'center' }}><CircularProgress sx={{ color:IOS.blue }} /></Box>
              : filtradosRedist.length > 0
                ? <>
                    {/* Móvil — cards */}
                    <Box sx={{ display:{ xs:'block', md:'none' } }}>
                      {datosRPag.map((row,i) => (
                        <Box className="almacen-product-card" key={i} sx={{ bgcolor:IOS.surface, borderRadius:'14px', p:2, mb:1.2,
                          border:`1px solid ${IOS.separator}` }}>
                          <Box className="pharmacy-layout" sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:1, mb:1.2 }}>
                            <Box sx={{ minWidth:0 }}>
                              <Typography sx={{ fontWeight:700, fontSize:'0.8rem', color:'#1F2937', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{row.PRODUCTO}</Typography>
                              <Typography sx={{ fontSize: '0.72rem', color:IOS.label2, fontFamily:'monospace' }}>{row.MEDCOD}</Typography>
                            </Box>
                          </Box>

                          <Box className="pharmacy-layout" sx={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:1, mb:1.2 }}>
                            {/* Donante */}
                            <Box sx={{ bgcolor:IOS.bg, borderRadius:'10px', p:1.2, border:`1px solid ${IOS.separator}` }}>
                              <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:IOS.label2, textTransform:'uppercase', mb:0.4 }}>Donante</Typography>
                              <Typography sx={{ fontSize:'0.74rem', fontWeight:700, color:'#1F2937', mb:0.6, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{row.DONANTE}</Typography>
                              <Box className="pharmacy-layout" sx={{ display:'flex', flexDirection:'column', gap:0.2 }}>
                                <Typography sx={{ fontSize: '0.72rem', color:'#4B5563' }}>Stock: <b style={{ color:'#1F2937' }}>{parseFloat(row.STOCK_DONANTE||0).toFixed(2)}</b></Typography>
                                <Typography sx={{ fontSize: '0.72rem', color:'#6B7280' }}>MSD {parseFloat(row.MSD_DONANTE||0).toFixed(2)} · CPMA {parseFloat(row.CPMA_DONANTE||0).toFixed(2)}</Typography>
                              </Box>
                            </Box>
                            {/* Receptor */}
                            <Box sx={{ bgcolor:IOS.bg, borderRadius:'10px', p:1.2, border:`1px solid ${IOS.separator}` }}>
                              <Box className="pharmacy-layout" sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:0.4 }}>
                                <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:IOS.label2, textTransform:'uppercase' }}>Receptor</Typography>
                                <IndPill valor={row.ESTADO_RECEPTOR} />
                              </Box>
                              <Typography sx={{ fontSize:'0.74rem', fontWeight:700, color:'#1F2937', mb:0.6, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{row.RECEPTOR}</Typography>
                              <Box className="pharmacy-layout" sx={{ display:'flex', flexDirection:'column', gap:0.2 }}>
                                <Typography sx={{ fontSize: '0.72rem', color:'#4B5563' }}>Stock: <b style={{ color:'#1F2937' }}>{parseFloat(row.STOCK_RECEPTOR||0).toFixed(2)}</b></Typography>
                                <Typography sx={{ fontSize: '0.72rem', color:'#6B7280' }}>MSD {parseFloat(row.MSD_RECEPTOR||0).toFixed(2)} · CPMA {parseFloat(row.CPMA_RECEPTOR||0).toFixed(2)}</Typography>
                              </Box>
                            </Box>
                          </Box>

                          <Box className="pharmacy-layout" sx={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:0.6 }}>
                            <Box sx={{ bgcolor:IOS.bg, borderRadius:'8px', px:0.5, py:0.6, textAlign:'center' }}>
                              <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:IOS.label2, textTransform:'uppercase' }}>Exceso</Typography>
                              <Typography sx={{ fontSize:'0.8rem', fontWeight:700, color:IOS.label }}>{parseFloat(row.Exceso||0).toFixed(2)}</Typography>
                            </Box>
                            <Box sx={{ bgcolor:'#FEE2E2', borderRadius:'8px', px:0.5, py:0.6, textAlign:'center' }}>
                              <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:'#991B1B', textTransform:'uppercase' }}>Sugerido Traspaso</Typography>
                              <Typography sx={{ fontSize:'0.9rem', fontWeight:800, color:IOS.red }}>{parseFloat(row.SUGERIDO_TRASPASO||0).toFixed(2)}</Typography>
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Box>

                    {/* Desktop — tabla */}
                    <Box sx={{ display:{ xs:'none', md:'block' } }}>
                    <Box sx={{ borderRadius:'14px', border:`1px solid ${IOS.separator}`, overflow:'hidden' }}>
                      <Box className="pharmacy-scroll" tabIndex={0} role="region" aria-label="Tabla de resultados, desplazamiento horizontal" sx={{ overflowX:'auto', overflowY:'auto', maxHeight:'calc(100vh - 360px)',
                        '&::-webkit-scrollbar':{ width:4, height:4 },
                        '&::-webkit-scrollbar-thumb':{ bgcolor:IOS.separator, borderRadius:4 } }}>
                        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.73rem', tableLayout:'fixed', minWidth:900 }}>
                          <colgroup>
                            <col style={{ width:'72px' }} />
                            <col style={{ width:'auto' }} />
                            <col style={{ width:'130px' }} />
                            <col style={{ width:'85px' }} />
                            <col style={{ width:'85px' }} />
                            <col style={{ width:'85px' }} />
                            <col style={{ width:'130px' }} />
                            <col style={{ width:'110px' }} />
                            <col style={{ width:'85px' }} />
                            <col style={{ width:'85px' }} />
                            <col style={{ width:'85px' }} />
                            <col style={{ width:'75px' }} />
                            <col style={{ width:'80px' }} />
                          </colgroup>
                          <thead>
                            <tr>
                              {[
                                {label:'Código'},{label:'Producto'},
                                {label:'Donante'},{label:'MSD Don.',right:true},
                                {label:'CPMA Don.',right:true},{label:'Stock Don.',right:true},
                                {label:'Receptor'},{label:'Estado'},
                                {label:'MSD Rec.',right:true},{label:'Stock Rec.',right:true},
                                {label:'CPMA Rec.',right:true},{label:'Exceso',right:true},
                                {label:'Sugerido',right:true},
                              ].map((h,i) => (
                                <th key={i} style={{ padding:'9px 10px', textAlign:h.right?'right':'left',
                                  fontSize: '0.72rem', fontWeight:700, color:IOS.label2, letterSpacing:'0.06em',
                                  textTransform:'uppercase', backgroundColor:IOS.surface,
                                  borderBottom:`1px solid ${IOS.separator}`, position:'sticky', top:0,
                                  whiteSpace:'nowrap', zIndex:1, overflow:'hidden', textOverflow:'ellipsis' }}>{h.label}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {datosRPag.map((row,i) => {
                              
                              return (
                                <tr key={i} style={{ borderBottom:`1px solid ${IOS.separator}`, transition:'background 0.1s', backgroundColor:IOS.surface }}
                                  onMouseEnter={e=>e.currentTarget.style.backgroundColor=IOS.bg}
                                  onMouseLeave={e=>e.currentTarget.style.backgroundColor=IOS.surface}>
                                  <td style={{ padding:'7px 10px', fontFamily:'monospace', fontWeight:600, color:'#374151', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{row.MEDCOD}</td>
                                  <td style={{ padding:'7px 10px', color:'#1F2937', fontWeight:600, fontSize:'0.7rem', lineHeight:1.3, wordBreak:'break-word' }}>{row.PRODUCTO}</td>
                                  <td style={{ padding:'7px 10px', color:'#1F2937', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{row.DONANTE}</td>
                                  <td style={{ padding:'7px 10px', textAlign:'right', color:'#6B7280' }}>{parseFloat(row.MSD_DONANTE||0).toFixed(2)}</td>
                                  <td style={{ padding:'7px 10px', textAlign:'right', color:'#6B7280' }}>{parseFloat(row.CPMA_DONANTE||0).toFixed(2)}</td>
                                  <td style={{ padding:'7px 10px', textAlign:'right', fontWeight:700, color:'#1F2937' }}>{parseFloat(row.STOCK_DONANTE||0).toFixed(2)}</td>
                                  <td style={{ padding:'7px 10px', color:'#1F2937', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{row.RECEPTOR}</td>
                                  <td style={{ padding:'7px 10px' }}><IndPill valor={row.ESTADO_RECEPTOR} /></td>
                                  <td style={{ padding:'7px 10px', textAlign:'right', color:'#6B7280' }}>{parseFloat(row.MSD_RECEPTOR||0).toFixed(2)}</td>
                                  <td style={{ padding:'7px 10px', textAlign:'right', color:'#6B7280' }}>{parseFloat(row.STOCK_RECEPTOR||0).toFixed(2)}</td>
                                  <td style={{ padding:'7px 10px', textAlign:'right', color:'#6B7280' }}>{parseFloat(row.CPMA_RECEPTOR||0).toFixed(2)}</td>
                                  <td style={{ padding:'7px 10px', textAlign:'right', fontWeight:600, color:'#374151' }}>{parseFloat(row.Exceso||0).toFixed(2)}</td>
                                  <td style={{ padding:'7px 10px', textAlign:'right' }}>
                                    <Box sx={{ display:'inline-flex', px:1, py:0.25, borderRadius:'20px', bgcolor:'#FEE2E2', border:'1px solid #FCA5A5' }}>
                                      <Typography sx={{ fontSize:'0.72rem', fontWeight:800, color:'#991B1B' }}>
                                        {parseFloat(row.SUGERIDO_TRASPASO||0).toFixed(2)}
                                      </Typography>
                                    </Box>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </Box>
                    </Box>
                    </Box>

                    <Paginacion total={filtradosRedist.length} pagina={pgR} setPagina={setPgR} rpp={rppR} setRpp={setRppR} />
                  </>
                : !errores[2] && <Box className="pharmacy-card" sx={{ bgcolor:IOS.surface, borderRadius:'16px', py:8, textAlign:'center',
                    border:`1px solid ${IOS.separator}` }}>
                    <Typography sx={{ fontWeight:600, color:IOS.label, mb:0.5 }}>Sin traspasos sugeridos</Typography>
                    <Typography sx={{ color:IOS.label2, fontSize:'0.82rem' }}>{donante==='TODOS' ? 'No hay redistribuciones en este momento' : 'No hay resultados para el donante seleccionado'}</Typography>
                  </Box>
            }

            <ReportModalRedistribucion open={modalRedist} onClose={()=>setModalRedist(false)}
              datos={filtradosRedist} parametros={{ donante, usuario:user?.NombreCompleto||'Usuario' }} />
          </Box>
        )}
      </Box>

      {familiaModal && (() => {
        const maxStock = Math.max(...(familiaModal.data||[]).map(d=>d.stockAlmacen), 1);
        const totalStock = (familiaModal.data||[]).reduce((s,d)=>s+d.stockAlmacen,0);
        const RANK_COLORS = ['#FFD60A','#C0C0C0','#CD7F32'];
        return (
          <Dialog open onClose={()=>setFamiliaModal(null)} aria-labelledby="almacen-family-title" transitionDuration={reduced?0:180} maxWidth="sm" fullWidth
            PaperProps={{className:'almacen-family-dialog',sx:{borderRadius:'24px',m:2,width:'calc(100% - 32px)',maxHeight:'85dvh'}}}>
            <Box className="pharmacy-modal-panel pharmacy-layout pharmacy-card" onClick={e=>e.stopPropagation()}
              sx={{ bgcolor:IOS.surface, borderRadius:'20px', overflow:'hidden', width:'100%', maxWidth:'100%',
                maxHeight:'78vh', display:'flex', flexDirection:'column', boxShadow:'0 24px 60px rgba(0,0,0,0.25)' }}>

              {/* Header */}
              <Box className="pharmacy-layout" sx={{ px:2.5, pt:2.5, pb:2, borderBottom:`1px solid ${IOS.separator}`,
                display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:1.5 }}>
                <Box className="pharmacy-layout" sx={{ display:'flex', gap:1.3, minWidth:0 }}>
                  <Box className="pharmacy-layout" sx={{ width:38, height:38, borderRadius:'11px', bgcolor:'#FFF3CD', flexShrink:0,
                    display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#8B6914" strokeWidth="2" strokeLinecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                  </Box>
                  <Box sx={{ minWidth:0 }}>
                    <Typography id="almacen-family-title" sx={{ fontSize: '0.72rem', fontWeight:700, color:IOS.label2, letterSpacing:'0.07em', textTransform:'uppercase', mb:0.2 }}>
                      Otras Presentaciones {familiaModal.nivel==='ipress' ? 'en esta IPRESS' : 'en Almacén'}
                    </Typography>
                    <Typography sx={{ fontSize:'0.92rem', fontWeight:800, color:IOS.label, lineHeight:1.2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {familiaModal.producto}
                    </Typography>
                    <Typography sx={{ fontSize:'0.72rem', color:IOS.label2, fontFamily:'monospace' }}>{familiaModal.codigo}</Typography>
                  </Box>
                </Box>
                <Box className="pharmacy-layout pharmacy-action" component="button" aria-label="Cerrar presentaciones" onClick={()=>setFamiliaModal(null)}
                  sx={{ width:28, height:28, borderRadius:'50%', border:'none', bgcolor:'#E5E5EA', cursor:'pointer', flexShrink:0,
                    display:'flex', alignItems:'center', justifyContent:'center', transition:'all 0.15s', '&:hover':{ bgcolor:'#D1D1D6' } }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={IOS.label} strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </Box>
              </Box>

              {/* Ranking */}
              <Box sx={{ p:2.2, overflowY:'auto', flex:1,
                '&::-webkit-scrollbar':{ width:4 }, '&::-webkit-scrollbar-thumb':{ bgcolor:IOS.separator, borderRadius:4 } }}>
                {familiaModal.loading
                  ? <Box sx={{ py:5, textAlign:'center' }}><CircularProgress size={24} sx={{ color:'#F59E0B' }} /></Box>
                  : familiaModal.error ? <Alert severity="error">No pudimos consultar las presentaciones. Cierra e inténtalo de nuevo.</Alert> : !familiaModal.data?.length
                    ? <Box sx={{ py:4, textAlign:'center' }}>
                        <Typography sx={{ color:IOS.label3, fontSize:'0.82rem' }}>Sin stock en otras presentaciones</Typography>
                      </Box>
                    : <Box className="pharmacy-layout" sx={{ display:'flex', flexDirection:'column', gap:1.3 }}>
                        {familiaModal.data.map((d,i) => (
                          <Box key={i}>
                            <Box className="pharmacy-layout" sx={{ display:'flex', alignItems:'center', gap:1, mb:0.5 }}>
                              <Box className="pharmacy-layout" sx={{ width:20, height:20, borderRadius:'50%', flexShrink:0,
                                bgcolor:i<3?RANK_COLORS[i]:IOS.bg, display:'flex', alignItems:'center', justifyContent:'center' }}>
                                <Typography sx={{ fontSize: '0.72rem', fontWeight:800, color:i<3?'white':IOS.label2 }}>{i+1}</Typography>
                              </Box>
                              <Box sx={{ flex:1, minWidth:0 }}>
                                <Typography sx={{ fontSize:'0.78rem', fontWeight:600, color:IOS.label,
                                  overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                  {d.producto}
                                </Typography>
                                <Typography sx={{ fontSize: '0.72rem', color:IOS.label2, fontFamily:'monospace' }}>{d.codigoMed}</Typography>
                              </Box>
                              <Typography sx={{ fontSize:'0.9rem', fontWeight:800, color:IOS.label, flexShrink:0 }}>{d.stockAlmacen.toLocaleString()}</Typography>
                            </Box>
                            <Box sx={{ height:5, bgcolor:IOS.bg, borderRadius:4, overflow:'hidden', ml:3.6 }}>
                              <Box sx={{ width:`${(d.stockAlmacen/maxStock)*100}%`, height:'100%', bgcolor:'#F59E0B', borderRadius:4, transition:'width 0.4s ease' }} />
                            </Box>
                          </Box>
                        ))}
                      </Box>
                }
              </Box>

              {/* Footer con total */}
              {!familiaModal.loading && familiaModal.data?.length > 0 && (
                <Box className="pharmacy-layout" sx={{ px:2.5, py:1.6, borderTop:`1px solid ${IOS.separator}`, bgcolor:IOS.bg,
                  display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:IOS.label2, textTransform:'uppercase', letterSpacing:'0.05em' }}>
                    Total en otras presentaciones
                  </Typography>
                  <Typography sx={{ fontSize:'0.95rem', fontWeight:800, color:'#8B6914' }}>{totalStock.toLocaleString()}</Typography>
                </Box>
              )}
            </Box>
          </Dialog>
        );
      })()}
    </Box>
    </>
  );
};

export default Almacen;